import { pagItem } from "@app/interface/pag-item";
import { Company } from "../interface/company";
import { detItem55, NFe } from "../interface/NF-e";
import { detItem65, NFCe } from "../interface/NFC-e";
import { IOperationNcmRange, Operation } from "../interface/operation";
import { Part } from "../interface/part";
import { ReduceSumPipe } from "../shared/pipes/reduce-sum.pipe";
import { Money } from "./money";
import { Utilities } from "./utilities";
import { PAYMENT_METHODS } from "@app/shared/lists/payment-methods";

type cfopOrigins = "part" | "ncmRange" | "operation";

export class NfUtilities extends Utilities {

  /** Remove the special characters of two codes and evaluate if they are equals */
  static compareCodes(code1: string, code2: string): boolean {
    return NfUtilities.removeSpecialCharacters(`${code1}`).trim() === NfUtilities.removeSpecialCharacters(`${code2}`).trim();
  }

  static removeSpecialCharactersOf(object: any) {
    if (!object || Number.isNaN(object)) {
      return
    }

    if (typeof object === 'number') {
      return
    }

    if (typeof object === 'undefined') {
      return
    }

    if (typeof object === 'object') {
      // ignore Maps
      if (object.size) {
        return
      }

      // ignore DateObject
      if (object.getTime) {
        return
      }

      // interate arrays
      if (object.length) {
        object.forEach(item => NfUtilities.removeSpecialCharactersOf(item))
        return
      }
    }

    if (Object.keys(object).length) {
      Object.keys(object).forEach(key => {
        switch (typeof object[key]) {
          case 'string':
            if(key === 'cProd'){ 
              // avoiding future errors in invoices imported via XML. Dont't change the code!
              return
            }
            object[key] = this.removeSpecialCharacters(object[key]);
            break;
          // interate childs
          case 'object':
            if (object[key] === null) {
              // delete object[key]
            } else {
              NfUtilities.removeSpecialCharactersOf(object[key]);
            }
            break;
          default:
            return
        }
      });
      return
    }

  }

  static calculateICMS_STof(prod: detItem55['prod'] | detItem65['prod'], params: {
    part: Part;
    operationFull: Operation;
    itemCST: string;
  }) {
    const  { part, operationFull, itemCST } = params;
     
    const ICMS_STtoUse = this.getICMS_STToUse(part, operationFull)
      
    const ICMS_ST: Partial<detItem55['imposto']['ICMS']> = {
      vBCST: 0,
      vICMSST_icms: 0,
      pMVAST:  ICMS_STtoUse?.Aliquot ?? 0,
      pICMSST: ICMS_STtoUse?.mva ?? 0,
    }

    if (["10", "30", "70"].includes(itemCST)) {
      if (ICMS_STtoUse?.Aliquot) {
        ICMS_ST.pICMSST = ICMS_STtoUse.Aliquot;
        ICMS_ST.pMVAST = ICMS_STtoUse.mva || 0;
        ICMS_ST.vBCST = Money(prod.vProd + Utilities.calcPercentage(ICMS_ST.pMVAST, prod.vProd));
        ICMS_ST.vICMSST_icms = Money(Utilities.calcPercentage(ICMS_ST.pICMSST,  ICMS_ST.vBCST));
      }
    }

    return ICMS_ST
  }
  
  static calculateICMSof(prod: detItem55['prod'] | detItem65['prod'], params: {
    part: Part,
    companyCRT: Company['crt'],
    operationFull: Operation,
    isDevolution?: boolean,
    pRedBC?: number,
  }) {

    const ICMSToUse = this.getICMSToUse(params.part, params.operationFull);

    const CST = ICMSToUse?.ICMS?.CST;
    const pICMS = ICMSToUse?.ICMS?.Aliquot ?? 0;
    let vBC = 0;

    const ICMS: Partial<detItem55['imposto']['ICMS']> = {
      CST: CST,
      modBC: 0,
      orig: params.part.origin,
      pICMS
    }

    if (pICMS) {
      if (params.isDevolution) {
        if (CST == '900') {
          const readjusmentPercentage = params.pRedBC || 0;
          vBC = Utilities.roundNumber(prod.vProd - (prod.vProd * (readjusmentPercentage) / 100));
        }
      } else if (["2", "3"].includes(params.companyCRT)) {
        const { vProd = 0, vDesc = 0, vFrete = 0, vOutro_item = 0 } = prod;
        vBC = (vProd - vDesc) + vFrete + (Number(vOutro_item) || 0);
      }
    }

    if (CST == "500" || CST == "60" || CST == "30") {
      vBC = 0;
    }

    ICMS.vBC = Money(vBC);
    ICMS.vICMS_icms = Utilities.calcPercentage(pICMS, vBC);
    return ICMS as detItem55['imposto']['ICMS']
  }

  static getICMSToUse(partFull: Part, operationFull: Operation): {
    ICMS: Part["ICMS"] | Operation["ICMS"];
    cfopOrigin: cfopOrigins;
    cfop_iOrigin: cfopOrigins;
    origin: "partICMS" | "ncmRangeFromPart" | "operationICMS";
  } {
    let ncmRangeFromPart: IOperationNcmRange;

    if (operationFull?.ncmRange && operationFull?.ncmRange?.length && partFull?.NCM) {
      ncmRangeFromPart = operationFull.ncmRange.find(ncmRange => ((ncmRange.ncmStart <= partFull.NCM) && (ncmRange.ncmEnd >= partFull.NCM)) && ncmRange.ICMS);
    }

    let ICMS: Part["ICMS"] | Operation["ICMS"] = { ...operationFull?.ICMS };
    let origin: "partICMS" | "ncmRangeFromPart" | "operationICMS" = "operationICMS";

    if (partFull && partFull?.ICMS?.CST) {
      ICMS = { ...partFull?.ICMS };
      origin = "partICMS";
    } else if (ncmRangeFromPart && ncmRangeFromPart?.ICMS?.CST) {
      ICMS = { ...ncmRangeFromPart?.ICMS };
      origin = "ncmRangeFromPart";
    }

    const cfopData = this._getICMSCFOPToUse(partFull?.ICMS?.CFOP, ncmRangeFromPart?.ICMS?.CFOP, operationFull?.ICMS?.CFOP);  
    const cfop_iData = this._getICMSCFOPT_IoUse(partFull?.ICMS?.CFOP_I, ncmRangeFromPart?.ICMS?.CFOP_I, operationFull?.ICMS?.CFOP_I);  

    ICMS["CFOP"] = cfopData.CFOP;
    ICMS["CFOP_I"] = cfop_iData.CFOP_I;

    return { ICMS, cfopOrigin: cfopData.origin, cfop_iOrigin: cfop_iData.origin, origin };
  }

  private static _getICMSCFOPToUse(
    partCFOP: Part["ICMS"]["CFOP"], 
    ncmRangeCFOP: IOperationNcmRange["ICMS"]["CFOP"], 
    operationCFOP: Operation["ICMS"]["CFOP"]
  ): { CFOP: Part["ICMS"]["CFOP"] | IOperationNcmRange["ICMS"]["CFOP"] | Operation["ICMS"]["CFOP"], origin: cfopOrigins } {
    if (partCFOP?.trim()) {
      return { CFOP: partCFOP?.trim(), origin: "part"};
    } else if (ncmRangeCFOP?.trim()) {
      return { CFOP: ncmRangeCFOP?.trim(), origin: "ncmRange"};
    } else {
      return { CFOP: operationCFOP?.trim(), origin: "operation"};
    }
  }

  private static _getICMSCFOPT_IoUse(
    partCFOP_I: Part["ICMS"]["CFOP_I"], 
    ncmRangeCFOP_I: IOperationNcmRange["ICMS"]["CFOP_I"], 
    operationCFOP_I: Operation["ICMS"]["CFOP_I"]
  ): { CFOP_I: Part["ICMS"]["CFOP_I"] | IOperationNcmRange["ICMS"]["CFOP_I"] | Operation["ICMS"]["CFOP_I"], origin: cfopOrigins } {
    if (partCFOP_I?.trim()) {
      return { CFOP_I: partCFOP_I?.trim(), origin: "part"};
    } else if (ncmRangeCFOP_I?.trim()) {
      return { CFOP_I: ncmRangeCFOP_I?.trim(), origin: "ncmRange"};
    } else {
      return { CFOP_I: operationCFOP_I?.trim(), origin: "operation"};
    }
  }
  
  static getPISToUse(partFull: Part, operationFull: Operation): Part["PIS"] | Operation["PIS"] {
    let PIS: Part["PIS"] | Operation["PIS"];

    if (partFull && partFull?.PIS?.CST) {
      PIS = partFull.PIS;
    } else if (operationFull?.ncmRange && operationFull.ncmRange?.length && partFull?.NCM) {
      const ncmRangeFromPart = operationFull.ncmRange.find(ncmRange => ((ncmRange.ncmStart <= partFull.NCM) && (ncmRange.ncmEnd >= partFull.NCM)) && ncmRange.PIS);

      if (ncmRangeFromPart?.PIS?.CST?.trim()) {
        PIS = ncmRangeFromPart.PIS;
      }
    }

    return PIS ?? operationFull.PIS;
  }

  static getCOFINSToUse(partFull: Part, operationFull: Operation): Part["COFINS"] | Operation["COFINS"] {
    let COFINS: Part["COFINS"] | Operation["COFINS"];

    if (partFull && partFull?.COFINS?.CST) {
      COFINS = partFull.COFINS;
    } else if (operationFull?.ncmRange && operationFull?.ncmRange?.length && partFull?.NCM) {
      const ncmRangeFromPart = operationFull.ncmRange.find(ncmRange => ((ncmRange.ncmStart <= partFull.NCM) && (ncmRange.ncmEnd >= partFull.NCM)) && ncmRange.COFINS);

      if (ncmRangeFromPart?.COFINS?.CST?.trim()) {
        COFINS = ncmRangeFromPart.COFINS;
      }
    }

    return COFINS ?? operationFull.COFINS;
  }

  static getIPIToUse(partFull: Part, operationFull: Operation): string {
    let IPI: string;
    
    if (partFull && partFull?.IPI?.trim()) {
      IPI = partFull.IPI;
    } else if (operationFull?.ncmRange && operationFull?.ncmRange?.length && partFull?.NCM) {
      const ncmRangeFromPart = operationFull.ncmRange.find(ncmRange => ((ncmRange.ncmStart <= partFull.NCM) && (ncmRange.ncmEnd >= partFull.NCM)) && ncmRange.IPI);

      if (ncmRangeFromPart?.IPI?.trim()) {
        IPI = ncmRangeFromPart.IPI;
      }
    }

    return IPI ?? operationFull.IPI;
  }

  static getSuperSimplesToUse(partFull: Part, operationFull: Operation): Part["superSimples"] | Operation["superSimples"] {
    let ncmRangeFromPart: IOperationNcmRange;

    if (operationFull?.ncmRange && operationFull?.ncmRange?.length && partFull?.NCM) {
      ncmRangeFromPart = operationFull.ncmRange.find(ncmRange => ((ncmRange.ncmStart <= partFull.NCM) && (ncmRange.ncmEnd >= partFull.NCM)) && ncmRange.superSimples);
    }
    
    return partFull?.superSimples ?? ncmRangeFromPart?.superSimples ?? operationFull?.superSimples;
  }

  static getICMS_STToUse(partFull: Part, operationFull: Operation): Part["ICMS_ST"] {
    let ncmRangeFromPart: IOperationNcmRange;

    if (operationFull?.ncmRange && operationFull?.ncmRange?.length && partFull?.NCM) {
      ncmRangeFromPart = operationFull.ncmRange.find(ncmRange => ((ncmRange.ncmStart <= partFull.NCM) && (ncmRange.ncmEnd >= partFull.NCM)) && ncmRange.ICMS_ST);
    }

    const ICMS_ST: Part["ICMS_ST"] = {
      Aliquot: partFull?.ICMS_ST?.Aliquot ?? ncmRangeFromPart?.ICMS_ST?.Aliquot,
      mva: partFull?.ICMS_ST?.mva ?? ncmRangeFromPart?.ICMS_ST?.mva,
    };

    return ICMS_ST;
  }

  static getIRPJToUse(partFull: Part, operationFull: Operation): Part["IRPJ"] | Operation["IRPJ"] {
    let ncmRangeFromPart: IOperationNcmRange;

    if (operationFull?.ncmRange && operationFull?.ncmRange?.length && partFull?.NCM) {
      ncmRangeFromPart = operationFull.ncmRange.find(ncmRange => ((ncmRange.ncmStart <= partFull.NCM) && (ncmRange.ncmEnd >= partFull.NCM)) && ncmRange.IRPJ);
    }

    const IRPJ: Part["IRPJ"] | Operation["IRPJ"] = {
      aliquotLabor: ncmRangeFromPart?.IRPJ?.aliquotLabor ?? operationFull?.IRPJ?.aliquotLabor,
      aliquotPart: partFull?.IRPJ?.aliquotPart ?? ncmRangeFromPart?.IRPJ?.aliquotPart ?? operationFull?.IRPJ?.aliquotPart,
      msg: partFull?.IRPJ?.msg ?? ncmRangeFromPart?.IRPJ?.msg ?? operationFull?.IRPJ?.msg,
      valMinRet: partFull?.IRPJ?.valMinRet ?? ncmRangeFromPart?.IRPJ?.valMinRet ?? operationFull?.IRPJ?.valMinRet,
    };

    return IRPJ;
  }

  static getINSSToUse(partFull: Part, operationFull: Operation): Part["INSS"] | Operation["INSS"] {
    let ncmRangeFromPart: IOperationNcmRange;

    if (operationFull?.ncmRange && operationFull?.ncmRange?.length && partFull?.NCM) {
      ncmRangeFromPart = operationFull.ncmRange.find(ncmRange => ((ncmRange.ncmStart <= partFull.NCM) && (ncmRange.ncmEnd >= partFull.NCM)) && ncmRange.INSS);
    }

    const INSS: Part["INSS"] | Operation["INSS"] = {
      aliquot: partFull?.INSS?.aliquot ?? ncmRangeFromPart?.INSS?.aliquot ?? operationFull?.INSS?.aliquot,
      msg: partFull?.INSS?.msg ?? ncmRangeFromPart?.INSS?.msg ?? operationFull?.INSS?.msg,
      valMinRet: partFull?.INSS?.valMinRet ?? ncmRangeFromPart?.INSS?.valMinRet ?? operationFull?.INSS?.valMinRet,
    }

    return INSS;
  }

  static getCSLLToUse(partFull: Part, operationFull: Operation): Part["CSLL"] | Operation["CSLL"] {
    let ncmRangeFromPart: IOperationNcmRange;

    if (operationFull?.ncmRange && operationFull?.ncmRange?.length && partFull?.NCM) {
      ncmRangeFromPart = operationFull.ncmRange.find(ncmRange => ((ncmRange.ncmStart <= partFull.NCM) && (ncmRange.ncmEnd >= partFull.NCM)) && ncmRange.CSLL);
    }

    const CSLL: Part["CSLL"] | Operation["CSLL"] = {
      aliquot: partFull?.CSLL?.aliquot ?? ncmRangeFromPart?.CSLL?.aliquot ?? operationFull?.CSLL?.aliquot,
      msg: partFull?.CSLL?.msg ?? ncmRangeFromPart?.CSLL?.msg ?? operationFull?.CSLL?.msg,
      valMinRet: partFull?.CSLL?.valMinRet ?? ncmRangeFromPart?.CSLL?.valMinRet ?? operationFull?.CSLL?.valMinRet,
    };

    return CSLL;
  }

  static calculatePISof(params: {
    pisConfig: Operation['PIS'] | Part['PIS'] | undefined,
    vBC_pis: number
  }): detItem55['imposto']['PIS'] | null {
    if (params && params.pisConfig?.CST) {
      const vPIS = Utilities.calcPercentage(params.pisConfig.Aliquot, params.vBC_pis);
      return {
        CST_pis: params.pisConfig.CST,
        pPIS: params.pisConfig.Aliquot || 0,
        vBC_pis: params.vBC_pis,
        vPIS
      }
    }
    return null
  }

  static calculateCOFINSof(params: {
    cofinsConfig: Operation['COFINS'] | Part['COFINS'] | undefined,
    vBC_cofins: number
  }): detItem55['imposto']['COFINS'] | null {
    if (params && params.cofinsConfig) {
      const vCOFINS = Utilities.calcPercentage(params.cofinsConfig.Aliquot, params.vBC_cofins);
      return {
        CST_cofins: params.cofinsConfig.CST,
        pCOFINS: params.cofinsConfig.Aliquot || 0,
        vBC_cofins: params.vBC_cofins,
        vCOFINS
      }
    }
    return null
  }

  static calculateIPIof(CST_IPI: string): detItem55['imposto']['IPI'] | null {
    if (CST_IPI) {
      const cEnq = ["00", "49", "99"].includes(CST_IPI) ? null : "";
      return {
        CSTIPI: {
          CST_IPI: CST_IPI as any,
        },
        cEnq
      }
    }
    return null
  }

  static sumICMStotOfNFeItems(detItems: detItem55[]) {
    const reducer = new ReduceSumPipe();
    return {
      vCOFINS_ttlnfe: Money(reducer.transform(detItems, (item) => Number(item.imposto?.COFINS?.vCOFINS))),
      vFrete_ttlnfe: Money(reducer.transform(detItems, (item) => Number(item.prod.vFrete))),
      vICMS_ttlnfe: Money(reducer.transform(detItems, (item) => Number(item.imposto?.ICMS?.vICMS_icms))),
      vIPI_ttlnfe: Money(reducer.transform(detItems, (item) => Number(item.imposto?.IPI?.CSTIPI?.vIPI))),
      vPIS_ttlnfe: Money(reducer.transform(detItems, (item) => Number(item.imposto?.PIS?.vPIS))),
      vSeg_ttlnfe: Money(reducer.transform(detItems, (item) => Number(item.prod.vSeg))),
      vST_ttlnfe: Money(reducer.transform(detItems, (item) => Number(item.imposto?.ICMS?.vICMSST_icms))),
      vOutro: Money(reducer.transform(detItems, (item) => Number(item.prod.vOutro_item))),
    }
  }

  static sumICMStotOfNFCeItems(detItems: detItem65[]) {
    const reducer = new ReduceSumPipe();
    return {
      vFrete_ttlnfe: Money(reducer.transform(detItems, (item) => Number(item.prod.vFrete))),
      vICMS_ttlnfe: Money(reducer.transform(detItems, (item) => Number(item.imposto?.ICMS?.vICMS_icms))),
      vSeg_ttlnfe: Money(reducer.transform(detItems, (item) => Number(item.prod.vSeg))),
      vST_ttlnfe: Money(reducer.transform(detItems, (item) => Number(item.imposto?.ICMS?.vICMSST_icms))),
      vOutro: Money(reducer.transform(detItems, (item) => Number(item.prod.vOutro_item))),
    }
  }



  /** @deprecated Use minor functions instead */
  static calculateTotals(invoice: NFe | NFCe, isDevolution?: boolean): (NFe | NFCe) {
    let netValue = 0
    // let taxes = 0;
    let totalICMS = 0
    let totalPis = 0;
    let totalIPI = 0;
    let totalCofins = 0;
    let totalST = 0;
    let totalDiscount = 0;
    // const isSupersimples = invoice.emit.CRT === 1;

    for (const item of invoice.det) {
      let sum = 0;

      // cast string values to number
      item.prod.vFrete = Number(item.prod.vFrete) || 0;

      // calc gross value
      item.prod.vProd = Money(item.prod.vUnTrib * item.prod.qTrib) || 0;
      netValue += item.prod.vProd;

      totalDiscount += (item.prod.vDesc || 0);


      const icmsAliquot = Number(item.imposto.ICMS.pICMS);
      const cst = item.imposto?.ICMS?.CST;

      if (!icmsAliquot || (cst == "60" || cst == "500" || cst == "30")) {
        // Produtos com alíquota zero não devem informar BC
        item.imposto.ICMS.vBC = 0;
      } else if (isDevolution) {
        if (item.imposto.ICMS.CST == '900') {
          const readjusmentPercentage = item.imposto.ICMS.pRedBC || 0;
          item.imposto.ICMS.vBC = Utilities.roundNumber(item.prod.vProd - (item.prod.vProd * (readjusmentPercentage) / 100));
        }
        // else: keep values of original invoice
      } else {
        const { vProd = 0, vDesc = 0, vFrete = 0, vOutro_item = 0 } = item.prod;
        const vBC = (vProd - vDesc) + vFrete + (Number(vOutro_item) || 0);
        item.imposto.ICMS.vBC = Money(vBC);
      }

      // calc ICMS values
      item.imposto.ICMS.vICMS_icms = Utilities.roundNumber(icmsAliquot ? (item.imposto.ICMS.vBC * icmsAliquot / 100) : (Number(item.imposto.ICMS['vICMS']) || Number(item.imposto.ICMS.vICMS_icms)));
      item.imposto.ICMS.pRedBC = 0;
      totalICMS += item.imposto.ICMS.vICMS_icms;

      // calc PIS value
      if ((item as detItem55).imposto.PIS && (item as detItem55).imposto.PIS.CST_pis) {
        (item as detItem55).imposto.PIS.vPIS = Utilities.roundNumber((item as detItem55).imposto.PIS.pPIS ? (item.prod.vProd * (item as detItem55).imposto.PIS.pPIS / 100) : 0);
        totalPis += (item as detItem55).imposto.PIS.vPIS;
        sum += (item as detItem55).imposto.PIS.vPIS;
      } else {
        delete item.imposto['PIS'];
      }

      // calc COFINS value
      if ((item as detItem55).imposto.COFINS && (item as detItem55).imposto.COFINS.CST_cofins) {
        (item as detItem55).imposto.COFINS.vCOFINS = Utilities.roundNumber((item as detItem55).imposto.COFINS.pCOFINS ? (item.prod.vProd * (item as detItem55).imposto.COFINS.pCOFINS / 100) : 0);
        totalCofins += (item as detItem55).imposto.COFINS.vCOFINS;
        sum += (item as detItem55).imposto.COFINS.vCOFINS;
      } else {
        delete item.imposto['COFINS'];
      }

      // calc IPI value
      const ipi_value = Utilities.roundNumber((item as detItem55).imposto.IPI && (item as detItem55).imposto.IPI.CSTIPI && Number.isFinite((item as detItem55).imposto.IPI.CSTIPI.vIPI) ? (item as detItem55).imposto.IPI.CSTIPI.vIPI : 0);
      totalIPI += ipi_value;
      sum += ipi_value;

      // calc SuperSimple
      item.imposto.ICMS.vCredICMSSN = Utilities.roundNumber((item.imposto.ICMS.pCredSN ? (item.prod.vProd * item.imposto.ICMS.pCredSN / 100) : 0));
      sum += item.imposto.ICMS.vCredICMSSN;

      if (isDevolution && item.imposto.ICMS.CST === "900") {
        // calc ICMS_ST value
        let originalItem = invoice.det.find(det => det.prod.NCM === item.prod.NCM);
        item.imposto.ICMS.vBCST = Number((Number(item.imposto.ICMS.vBCST) / item.prod.qTrib).toFixed(2));
        item.imposto.ICMS.vICMSST_icms = Number((Number(item.imposto.ICMS.vICMSST_icms || item.imposto.ICMS['vICMSST']) || 0 / item.prod.qTrib).toFixed(2));
      } else if (!["10", "30", "70"].includes(item.imposto.ICMS.CST)) {
        /** rejection 553: Total da BC ICMS-ST difere do somatório dos itens, only need to calculate it when the CST is 10, 30 or 70 */
        item.imposto.ICMS.vBCST = 0;
        item.imposto.ICMS.vICMSST_icms = 0;
      }

      totalST += (item.imposto.ICMS.vICMSST_icms || 0);
      sum += item.imposto.ICMS.vICMSST_icms;
      // taxes += sum;

      /**
       * Rejeição: Total do Valor Aproximado dos Tributos difere do
       * somatório dos itens
       */
      item.imposto.vTotTrib = null;
    }

    const reducer = new ReduceSumPipe();
    const detArray = invoice.det as detItem55[];

    // sum freigth
    const freight_value = reducer.transform(detArray, (item) => Number(item.prod.vFrete));

    // sum others
    const other_expenses = reducer.transform(detArray, (item) => Number(item.prod.vOutro_item));


    // sum insurance
    const insurance_value = reducer.transform(detArray, (item) => Number(item.prod.vSeg));

    // sum BC_ST
    const bcst = reducer.transform(detArray, (item) => Number(item.imposto.ICMS.vBCST));

    const ipiDevol = NfUtilities.getPropertyOf(invoice, 'vIPIDevol');

    // sum BC_icms
    let vBC = reducer.transform(detArray, (item) => Number(item.imposto.ICMS.vBC));

    // sum taxes
    invoice['total']['ICMStot'] = {
      vICMS_ttlnfe: Money(totalICMS),
      vPIS_ttlnfe: Money(totalPis),
      vIPI_ttlnfe: Money(totalIPI),
      vCOFINS_ttlnfe: Money(totalCofins),
      vST_ttlnfe: Money(totalST),
      vProd_ttlnfe: Money(netValue),
      // vTotTrib_ttlnfe: Utilities.roundNumber(taxes),
      vBC_ttlnfe: Money(vBC),
      vDesc_ttlnfe: Money(totalDiscount),
      vNF: Money(netValue - totalDiscount),
      vII_ttlnfe: 0,
      vFCPSTRet_ttlnfe: 0,
      vFCPST_ttlnfe: 0,
      vFCP_ttlnfe: 0,
      vIPIDevol_ttlnfe: Money(ipiDevol),
      vOutro: Money(other_expenses),
      vFrete_ttlnfe: Money(freight_value),
      vBCST_ttlnfe: Money(bcst),
      vSeg_ttlnfe: Money(insurance_value),
    }

    delete invoice.total['ICMSTot'];

    invoice.total.ICMStot.vNF = Money(netValue
      + (freight_value || 0)
      + (insurance_value || 0)
      + (other_expenses || 0)
      + (totalST ?? 0)
      - (totalDiscount || 0));

    if (isDevolution) {
      invoice.total.ICMStot.vNF += (totalST || 0);
    }

    /** Fix rejection: Ausência de troco quando o valor dos pagamentos informados for maior que o total da nota */
    if (!isDevolution && invoice.pag && invoice.pag.length) {
      Utilities.fixCentsDifference(invoice.pag, "vPag", invoice.total.ICMStot.vNF)
    }

    return invoice
  }

  public static getPropertyOf(invoice: NFe | NFCe, property: string) {
    if (!invoice) {
      return 0
    }

    const tot = invoice['total']['ICMStot'] || invoice['total']['ICMSTot'];

    if (invoice.det && invoice.det.length) {
      switch (property) {
        case "vOutro":
          return invoice.det
            .slice()
            .map(detItem => detItem.prod ? (Number.isFinite(detItem.prod.vOutro_item as any) ? Number(detItem.prod.vOutro_item) : Number(detItem.prod['vOutro']) || 0) : 0)
            .reduce((a, b) => a + b)
        case "vBCST":
          return invoice.det
            .slice()
            .map(detItem => {
              return detItem.imposto && detItem.imposto.ICMS ? (detItem.imposto.ICMS.vBCST || 0) : 0
            }).reduce((a, b) => a + b)
        case "vFrete":
          return invoice.det
            .slice()
            .map(detItem => detItem.prod ? Number(detItem.prod.vFrete) || 0 : 0)
            .reduce((a, b) => a + b)
        case "vSeg":
          return invoice.det
            .slice()
            .map(detItem => detItem.prod ? (detItem.prod.vSeg || 0) : 0)
            .reduce((a, b) => a + b)
        case "vIPIDevol": return Number(tot['vIPIDevol_ttlnfe'] || tot['vIPIDevol']) || 0
        default:
          return 0
      }
    }
    return 0

  }

  public static getNatOp(company: Company, clientRetISS: boolean): number | undefined {
    if (clientRetISS && company.exigibilidadeRetIss) {
      return +company.exigibilidadeRetIss;
    } else if (company.exigibilidade) {
      return +company.exigibilidade;
    } else {
      return 1;
    }
  }

  public static getTagCard(pag: pagItem, isNFCe: boolean): pagItem['card'] | null {
    if(pag.indPag_pag == '0') {
      // dados de combrança não devem ser informados para pagamento a vista
      return null
    }

    switch (pag.tPag) {
      case PAYMENT_METHODS.credit:
      case PAYMENT_METHODS.debit:
        return { tipoIntegracao: 2 };
      case PAYMENT_METHODS.dynamicPix:
        if(isNFCe) {
          return { tipoIntegracao: 2 };
        }
        return null;
      case PAYMENT_METHODS.staticPix:
      default:
        return null;
    }
  }
}
